import { Component, Host, h, Prop, Listen, Element, State } from '@stencil/core';
import { handleButtonKeydown, handleLinkClick, handleLinkKeydown, projectTemplate } from "../../utils/utils";

@Component({
  tag: 'me-button',
  styleUrl: 'me-button.scss',
  shadow: false,
})
export class MeButton {

  private static nextButtonID = 1;
  private readonly buttonID = MeButton.nextButtonID++;

  @Element() host: HTMLElement;

  @Prop() size = 'r';
  @Prop() theme = 'primary';
  @Prop() variant = 'normal';
  @Prop() icons = false;
  @Prop({ reflect: true }) disabled = false;
  @Prop() round = false;
  @Prop() inline = true;
  @Prop() loading = false;
  @Prop() type: 'button' | 'submit' | 'reset' = 'button';
  @Prop({ reflect: true }) href?: string;
  @Prop() target = '_self';

  @State() submitTemplate?: HTMLTemplateElement = null;

  private hiddenSubmit = false;

  private get submitInput(): HTMLInputElement | null {
    return this.host.querySelector(`
      :scope .me-button__label > span > input[type=submit],
      :scope .me-button__label > span > *:not([hidden]) > input[type=submit]
    `);
  }

  private get isButton() {
    return !Boolean(this.href);
  }

  @Listen('keydown')
  handleKeyDown(event: KeyboardEvent) {
    if (this.isButton) {
      handleButtonKeydown(event, this.host);
    }
    else {
      handleLinkKeydown(event, this.host);
    }
  }

  @Listen('click')
  handleClick(event: MouseEvent) {
    if (this.disabled) {
      event.stopImmediatePropagation();
      return;
    }
    if (this.isButton) {
      const form = this.host.closest('form');
      if (!form) return;
      switch (this.type) {
        case 'submit': {
          if (form.noValidate || form.checkValidity()) {
            this.submitInput?.click();
          } else {
            event.preventDefault();
            event.stopPropagation();
          }
          break;
        }
        case 'reset': {
          form.reset();
          break;
        }
      }
    } else {
      handleLinkClick(this.href, this.target, event)
    }
  }

  componentWillLoad() {
    this.hiddenSubmit = Boolean(this.host.querySelector('[slot=submit][hidden]'));
  }

  render() {
    const classList = [
      'me-button',
      `me-button--${this.size}`,
      `me-button--${this.theme}`,
      `me-button--${this.variant}`,
      this.icons ? 'me-button--icons' : null,
      this.round ? 'me-button--round' : null,
      this.inline ? 'me-button--inline' : null,
      this.loading ? 'me-button--loading' : null,
      this.hiddenSubmit ? 'me-button--hidden-submit' : null,
    ].filter(c => !!c).join(' ');

    const labelID = `me-button-label-${this.buttonID}`;

    return (
      <Host
        class={classList}
        role={this.isButton ? 'button' : 'link'}
        aria-labelledby={labelID}
        tabindex="0"
      >
        <template ref={(el: HTMLTemplateElement) => this.submitTemplate = el}>
          <slot name="submit">
            <input type="submit" hidden={true} value="Absenden" />
          </slot>
        </template>
        <span class="me-button__icon me-button__icon--left">
          <slot name="icon-left">
            <i class="me-button__spinner fa-solid fa-spinner fa-spin"></i>
          </slot>
        </span>
        <span id={labelID} class="me-button__label">
          <slot></slot>
          {this.type === 'submit'
            ? <span class="me-button__submit" ref={projectTemplate(this.submitTemplate)}></span>
            : ''
          }
        </span>
        <span class="me-button__icon me-button__icon--right">
          <slot name="icon-right"></slot>
        </span>
      </Host>
    );
  }

}
